import { StyleSheet, Text, View } from '@react-pdf/renderer'

import { styles as pdfStyles } from '../pdfStyles'

const styles = StyleSheet.create({
  tableStyle: {
    display: 'flex',
    width: 'auto',
  },
  tableSubtitleStyle: {
    fontSize: 6,
    color: '#666666',
  },
  tableRowStyle: {
    flexDirection: 'row',
  },
  firstTableColHeaderStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '20%',
    height: 40,
    borderStyle: 'solid',
    borderColor: '#e4e4ed',
    borderBottomColor: '#e4e4ed',
    borderWidth: 1.5,
    borderTopLeftRadius: 5,
    backgroundColor: '#f5f5f7',
  },
  tableColHeaderStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '20%',
    borderStyle: 'solid',
    borderColor: '#e4e4ed',
    borderBottomColor: '#e4e4ed',
    borderWidth: 1.5,
    borderLeftWidth: 0,
    backgroundColor: '#f5f5f7',
  },
  firstTableColStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '20%',
    height: 40,
    borderStyle: 'solid',
    borderColor: '#e4e4ed',
    borderWidth: 1.5,
    borderTopWidth: 0,
  },
  tableColStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '20%',
    borderStyle: 'solid',
    borderColor: '#e4e4ed',
    borderWidth: 1.5,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHeaderStyle: {
    textAlign: 'center',
    margin: 4,
    fontSize: 12,
    color: '#75757d',
    fontWeight: 'light',
  },
  tableCellStyle: {
    margin: 5,
    color: 'black',
    fontSize: 10,
  },
  tableHeaders: {
    display: 'flex',
    flexDirection: 'row',
    fontSize: 8,
  },
})

const TableRoot = ({
  children,
  style,
}: {
  children: React.ReactNode
  style?: React.CSSProperties
}) => (
  <View style={[styles.tableStyle, ...(style ? [{ ...style } as any] : [])]}>
    {children}
  </View>
)

const TableLine = ({ style, ...rest }: { style?: React.CSSProperties }) => (
  <View
    style={[pdfStyles.line, ...(style ? [{ ...style } as any] : [])]}
    {...rest}
  />
)

const TableHeader = ({
  type = 'outlineTable',
  headers,
}: {
  headers: {
    label:
      | string
      | {
          labelTitle: string
          labelStyle?: React.CSSProperties
          labelSubtitle?: string
          labelSubtitleStyle?: React.CSSProperties
        }
    style?: React.CSSProperties
  }[]
  type?: 'table' | 'outlineTable'
}) => (
  <View
    style={type === 'outlineTable' ? styles.tableHeaders : styles.tableRowStyle}
    fixed
  >
    {headers.map((header, index) => (
      <View
        key={`header-${index}-${
          typeof header.label === 'string'
            ? header.label
            : header.label.labelTitle
        }`}
        style={
          type === 'outlineTable'
            ? {
                width: '20%',
                ...(header?.style as any),
              }
            : {
                ...(index === 0
                  ? styles.firstTableColHeaderStyle
                  : styles.tableColHeaderStyle),
                ...(index === headers.length - 1 && {
                  borderTopRightRadius: 5,
                }),
              }
        }
      >
        <Text
          style={
            type === 'outlineTable'
              ? {
                  ...(typeof header.label === 'object' &&
                    (header?.label?.labelStyle as any)),
                }
              : styles.tableCellHeaderStyle
          }
        >
          {typeof header.label === 'string'
            ? header.label
            : header.label.labelTitle}
          {typeof header.label === 'object' && header.label.labelSubtitle && (
            <Text style={styles.tableSubtitleStyle}>
              {header.label.labelSubtitle}
            </Text>
          )}
        </Text>
      </View>
    ))}
  </View>
)

const TableRow = ({
  type = 'outlineTable',
  rowItems,
  lastRow,
}: {
  type?: 'table' | 'outlineTable'
  rowItems: {
    label:
      | string
      | {
          labelTitle: string
          labelStyle?: React.CSSProperties
          labelSubtitle?: string
          labelSubtitleStyle?: React.CSSProperties
        }
    rowStyle?: React.CSSProperties
  }[]
  lastRow?: boolean
}) => {
  return (
    <View style={styles.tableRowStyle}>
      {rowItems.map((row, index) => (
        <View
          key={`row-${index}-${typeof row.label === 'object' ? row.label.labelTitle : row.label}`}
          style={
            type === 'outlineTable'
              ? {
                  paddingTop: 4,
                  paddingBottom: lastRow ? 5 : 10,
                  width: '20%',
                  ...(row.rowStyle as any),
                }
              : {
                  ...(index === 0
                    ? styles.firstTableColStyle
                    : styles.tableColStyle),
                  ...(lastRow &&
                    index === 0 && {
                      borderBottomLeftRadius: 5,
                    }),
                  ...(lastRow &&
                    index === rowItems.length - 1 && {
                      borderBottomRightRadius: 5,
                    }),
                }
          }
        >
          <Text
            style={
              type === 'outlineTable'
                ? {
                    ...(typeof row.label === 'object' &&
                      (row?.label?.labelStyle as any)),
                  }
                : styles.tableCellStyle
            }
          >
            {typeof row.label === 'object' ? row.label.labelTitle : row.label}{' '}
            {typeof row.label === 'object' && row.label.labelSubtitle && (
              <Text style={styles.tableSubtitleStyle}>
                {row.label.labelSubtitle}
              </Text>
            )}
          </Text>
        </View>
      ))}
    </View>
  )
}

export const Table = {
  Root: TableRoot,
  Header: TableHeader,
  Line: TableLine,
  Row: TableRow,
} as {
  Root: typeof TableRoot
  Header: typeof TableHeader
  Line: typeof TableLine
  Row: typeof TableRow
}
